import { FixMeAny } from 'types/global'
import { SearchLookupFormFieldPayload } from 'services/types/custom-form-field'
import { SalesPipelineByStatusPayload, GetListCustomLeadFormFieldPayload } from 'services/types/settings'
import { GetLeadOpportunitiesPayload, GetOpportunityListPayload } from 'services/types/opportunity'
import { GetLeadContactsPayload } from 'services/types/lead-contact'
import { GetListPayload as GetQuotationListPayload } from 'services/quotation'
import {
  GetHistoryLogPayload,
  GetLeadActivitiesPayload,
  CheckLeadDuplicatesV2Payload,
  GetLeadsPayload,
  GetLeadTasksPayload,
  GetMergeableLeadsPayload,
  GetLeadDuplicateListPayload,
} from 'services/types/lead'
import { GetCamsFitmentsPayload } from 'services/types/cams'
import { FiltersRequestPayload } from 'services/types/lookup'
import { AutomationDetailPayload, GetAutomationPayload } from 'services/types/automation'
import { GetListByProcessIdPayload, GetListByLeadStatusIdPayload } from 'services/disposition'
import { GetSalesObjectiveDetailPayload, GetSalesObjectivePayload } from 'services/sales-objective'
import {
  GetImportedFileDetailsPayload,
  GetImportHistoryListPayload,
  GetImportHistoryResultPayload,
} from 'services/types/import'
import { GetBulkLeadsUpdateHistory } from 'services/lead-bulk-operation'
import { GetAutomationConfigPayload, GetReadableInfixPayload } from 'services/pipeline-automation'
import { GetListPayload as GetApproversListPayload } from 'services/settings-approvers'
import { GetPriceItemAddOnsPayload } from 'services/price'

export const approverKey = {
  all: ['approvers'] as const,
  lists: () => [...approverKey.all, 'list'] as const,
  list: (payload: GetApproversListPayload) => [...approverKey.lists(), payload] as const,
  details: () => [...approverKey.all, 'detail'] as const,
  detail: (id?: string) => [...approverKey.details(), id] as const,
  permissionLevel: () => [...approverKey.all, 'permissionLevel'],
}

export const callStatusKey = {
  all: ['call-status'] as const,
  list: () => [...callStatusKey.all, 'list'] as const,
}

export const camsKey = {
  all: ['cams-fitments'] as const,
  lists: () => [...camsKey.all, 'list'] as const,
  list: (payload: Partial<GetCamsFitmentsPayload>) => [...camsKey.lists(), payload] as const,
  tabs: () => [...camsKey.all, 'tab'] as const,
  tab: (payload: string) => [...camsKey.tabs(), payload] as const,
}

export const customFieldKey = {
  all: ['customField'] as const,
  lists: () => [...customFieldKey.all, 'list'] as const,
  list: (payload: GetListCustomLeadFormFieldPayload) => [...customFieldKey.lists(), payload] as const,
}

export const externalApiKey = {
  all: ['external-api'] as const,
  lists: () => [...externalApiKey.all, 'list'] as const,
  list: (payload: FixMeAny) => [...externalApiKey.lists(), payload] as const,
  details: () => [...externalApiKey.all, 'detail'] as const,
  detail: (id: string) => [...externalApiKey.details(), id] as const,
}

export const pitchKey = {
  all: ['pitch'] as const,
  decks: () => [...pitchKey.all, 'decks'] as const,
  reference: () => [...pitchKey.all, 'reference'] as const,
  // Separate from reference() on purpose: reference is the active-only list the upload
  // picker binds to, this one includes retired industries for the Industries tab.
  industries: () => [...pitchKey.all, 'industries'] as const,
  globalVariables: () => [...pitchKey.all, 'globalVariables'] as const,
  log: (deckId: string, languageCode: string) => [...pitchKey.all, 'log', deckId, languageCode] as const,
  // Keyed by subject rather than by lead, matching the RM-facing methods: the
  // same keys serve Accounts when those are pitched.
  decksForSubject: (subjectType: string, subjectId: string) =>
    [...pitchKey.all, 'subjectDecks', subjectType, subjectId] as const,
}

export const searchLookupFormFieldKey = {
  all: ['searchLookupFormFieldKey'] as const,
  lists: () => [...searchLookupFormFieldKey.all, 'list'] as const,
  list: (payload: SearchLookupFormFieldPayload) => [...searchLookupFormFieldKey.lists(), payload] as const,
}

export const dispositionKey = {
  all: ['disposition'] as const,
  lists: () => [...dispositionKey.all, 'list'] as const,
  list: (payload: GetListByProcessIdPayload | GetListByLeadStatusIdPayload) =>
    [...dispositionKey.lists(), payload] as const,
}

export const meetingKey = {
  all: ['meeting'] as const,
  meetingStatus: () => [...meetingKey.all, 'status'] as const,
  meetingTemplate: () => [...meetingKey.all, 'template'] as const,
  meetingMinutePrefill: () => [...meetingKey.all, 'meetingMinutePrefill'] as const,
  detail: (id?: string) => [...meetingKey.all, 'detail', id] as const,
}

export const historyLogKey = {
  all: ['historyLog'] as const,
  lists: () => [...historyLogKey.all, 'list'] as const,
  list: (payload: GetHistoryLogPayload) => [...historyLogKey.lists(), payload] as const,
}

export const importHistoryKey = {
  all: ['import-history'] as const,
  lists: () => [...importHistoryKey.all, 'list'] as const,
  list: (payload: GetImportHistoryListPayload) => [...importHistoryKey.lists(), payload] as const,
  details: () => [...importHistoryKey.all, 'detail'] as const,
  detail: (payload: GetImportedFileDetailsPayload) => [...importHistoryKey.details(), payload] as const,
  results: () => [...importHistoryKey.all, 'result'] as const,
  result: (payload: GetImportHistoryResultPayload) => [...importHistoryKey.results(), payload] as const,
}

export const ingestionV2Key = {
  all: ['ingestion-v2'] as const,
  tasks: () => [...ingestionV2Key.all, 'task'] as const,
  task: (taskId: string) => [...ingestionV2Key.tasks(), taskId] as const,
  taskErrors: (taskId: string) => [...ingestionV2Key.task(taskId), 'errors'] as const,
  taskRows: (taskId: string, payload: { page: number; pageSize: number; outcome: string }) =>
    [...ingestionV2Key.task(taskId), 'rows', payload] as const,
  commonColumns: () => [...ingestionV2Key.all, 'common-columns'] as const,
  lists: () => [...ingestionV2Key.all, 'list'] as const,
  list: (payload: { page: number; pageSize: number; sortBy?: string; sortDir?: string }) =>
    [...ingestionV2Key.lists(), payload] as const,
}

export const leadActivityKey = {
  all: ['leadActivity'] as const,
  lists: () => [...leadActivityKey.all, 'list'] as const,
  list: (payload: GetLeadActivitiesPayload) => [...leadActivityKey.lists(), payload] as const,
}

export const productPriceKey = {
  all: ['product-price'] as const,
  lookup: () => [...productPriceKey.all, 'lookup'] as const,
  filter: () => [...productPriceKey.all, 'filter'] as const,
}

export const productGroupKey = {
  all: [...productPriceKey.all, 'product-group'] as const,
  list: (payload?: FixMeAny) => [...productGroupKey.all, 'list', payload] as const,
  lists: () => [...productGroupKey.all, 'list'] as const,
  detail: (id?: string) => [...productGroupKey.all, 'detail', id] as const,
}

export const productKey = {
  all: [...productPriceKey.all, 'product'] as const,
  list: (payload?: FixMeAny) => [...productKey.all, 'list', payload] as const,
  lists: () => [...productKey.all, 'list'] as const,
  detail: (id?: string) => [...productKey.all, 'detail', id] as const,
}

export const productServiceKey = {
  all: [...productPriceKey.all, 'service'] as const,
  list: (payload?: FixMeAny) => [...productServiceKey.all, 'list', payload] as const,
  lists: () => [...productServiceKey.all, 'list'] as const,
  detail: (id?: string) => [...productServiceKey.all, 'detail', id] as const,
}

export const productAddonKey = {
  all: [...productPriceKey.all, 'add-on'] as const,
  list: (payload?: FixMeAny) => [...productAddonKey.all, 'list', payload] as const,
  lists: () => [...productAddonKey.all, 'list'] as const,
  detail: (id?: string) => [...productAddonKey.all, 'detail', id] as const,
}

export const productBundleKey = {
  all: [...productPriceKey.all, 'bundle'] as const,
  list: (payload?: FixMeAny) => [...productBundleKey.all, 'list', payload] as const,
  lists: () => [...productBundleKey.all, 'list'] as const,
  detail: (id?: string) => [...productBundleKey.all, 'detail', id] as const,
}

export const priceKey = {
  all: [...productPriceKey.all, 'bundle'] as const,
  list: (payload?: FixMeAny) => [...priceKey.all, 'list', payload] as const,
  lists: () => [...priceKey.all, 'list'] as const,
  detail: (id?: string) => [...priceKey.all, 'detail', id] as const,
}

export const priceItemAddOnsKey = {
  all: ['price-item-add-ons'] as const,
  lists: () => [...priceItemAddOnsKey.all, 'list'] as const,
  list: (payload: GetPriceItemAddOnsPayload) => [...priceItemAddOnsKey.lists(), payload] as const,
}

export const quotationKey = {
  all: ['quotation'] as const,
  list: (payload?: GetQuotationListPayload) => [...quotationKey.all, 'list', payload] as const,
  lists: () => [...quotationKey.all, 'list'] as const,
  detail: (id?: string) => [...quotationKey.all, 'detail', id] as const,
  lookup: () => [...quotationKey.all, 'lookup'] as const,
}

export const pendingQuotationKey = {
  all: [...quotationKey.all, 'pending'] as const,
  list: (payload?: FixMeAny) => [...pendingQuotationKey.all, 'list', payload] as const,
  lists: () => [...pendingQuotationKey.all, 'list'] as const,
  detail: (id?: string) => [...pendingQuotationKey.all, 'detail', id] as const,
  lookup: () => [...pendingQuotationKey.all, 'lookup'] as const,
}

export const pendingQuotationApprovalListKey = {
  all: [...quotationKey.all, 'pending-quotation-approval-list'] as const,
  list: (payload?: FixMeAny) => [...pendingQuotationApprovalListKey.all, payload] as const,
}

export const leadEmailUnsentListKey = {
  all: ['lead-email-unsent'] as const,
  list: (payload?: FixMeAny) => [...leadEmailUnsentListKey.all, 'list', payload] as const,
}

export const notificationsKey = {
  all: ['notifications_'] as const,
  list: (payload?: FixMeAny) => [...notificationsKey.all, 'list', payload] as const,
}

export const notificationsCountKey = {
  all: ['notifications-count'] as const,
  list: (payload?: FixMeAny) => [...notificationsCountKey.all, 'list', payload] as const,
}

export const pipelineAutomationKey = {
  all: ['pipeline-automation'] as const,
  details: () => [...pipelineAutomationKey.all, 'detail'],
  detail: (id: string) => [...pipelineAutomationKey.details(), id],
  configs: () => [...pipelineAutomationKey.all, 'config'],
  config: (payload: GetAutomationConfigPayload) => [...pipelineAutomationKey.configs(), payload],
  readableInfix: (payload: GetReadableInfixPayload) => [...pipelineAutomationKey.all, 'readable-infix', payload],
  getJobStatus: (id: string) => [...pipelineAutomationKey.all, 'job-status', id],
  getJobStatuses: () => [...pipelineAutomationKey.all, 'job-status'],
}

export const automationKey = {
  all: ['automation'] as const,
  create: () => [...automationKey.all, 'create'] as const,
  update: () => [...automationKey.all, 'update'] as const,
  lists: () => [...automationKey.all, 'list'] as const,
  list: (filters: GetAutomationPayload) => [...automationKey.lists(), filters] as const,
  automationExecution: () => [...automationKey.all, 'automationExecution'] as const,
  automationCallType: () => [...automationKey.all, 'automationCallType'] as const,
  leadStatuses: () => [...automationKey.all, 'leadStatuses'] as const,
  leadCall: () => [...automationKey.all, 'leadCall'] as const,
  conditionType: () => [...automationKey.all, 'conditionType'] as const,
  automationDetails: () => [...automationKey.all, 'detail'] as const,
  automationDetail: (payload: AutomationDetailPayload) => [...automationKey.automationDetails(), payload] as const,
}

export const leadKey = {
  all: ['lead'] as const,
  lists: () => [...leadKey.all, 'list'] as const,
  list: (payload?: GetLeadsPayload) => [...leadKey.lists(), payload] as const,
  leadExist: () => [...leadKey.lists(), 'lead-exist'] as const,
  leadStat: () => [...leadKey.lists(), 'lead-stat'] as const,
  data: (id?: string) => [...leadKey.all, 'data', id] as const,
  detail: (id?: string) => [...leadKey.all, 'detail', id] as const,
  tab: (leadId?: string) => [...leadKey.detail(leadId), 'tab'] as const,
  tabV2: (leadId?: string) => [...leadKey.detail(leadId), 'tab-v2'] as const,
  detailTaskLists: (leadId: string) => [...leadKey.detail(leadId), 'tasks'] as const,
  detailTaskList: (payload: GetLeadTasksPayload) =>
    [...leadKey.detailTaskLists(payload.lead_id), payload.filter] as const,
  detailTaskLists_: (leadId: string) => [...leadKey.detail(leadId), 'tasks_'] as const,
  detailTaskList_: (payload: GetLeadTasksPayload) =>
    [...leadKey.detailTaskLists_(payload.lead_id), payload.filter] as const,

  lookup: () => [...leadKey.all, 'lookup'] as const,
}

// export const leadTaskKey = {
//   all: ['lead_tasks'] as const,
//   detailTaskLists: (leadId: string) => [...leadKey.detail(leadId), 'tasks'] as const,
//   detailTaskList: (payload: GetLeadTasksPayload) =>
//     [...leadTaskKey.detailTaskLists(payload.lead_id), payload.filter] as const,
// }

export const duplicateLeadMergeableKey = {
  all: ['lead-mergeable'] as const,
  lists: () => [...leadAddressKey.all, 'list'] as const,
  list: (payload?: GetMergeableLeadsPayload) => [...leadAddressKey.lists(), payload] as const,
}

export const leadMergeableKey = {
  all: ['lead-mergeable'] as const,
  lists: () => [...leadAddressKey.all, 'list'] as const,
  list: (payload?: GetMergeableLeadsPayload) => [...leadAddressKey.lists(), payload] as const,
}

export const leadAddressKey = {
  all: ['lead-address'] as const,
  lists: () => [...leadAddressKey.all, 'list'] as const,
  list: (payload?: string) => [...leadAddressKey.lists(), payload] as const,
}

export const leadAddressConfigKey = {
  all: ['lead-address-config'] as const,
  lists: () => [...leadAddressConfigKey.all, 'list'] as const,
  list: (payload?: string) => [...leadAddressConfigKey.lists(), payload] as const,
}

export const leadCampaignKey = {
  all: ['lead-campaign'] as const,
  lists: () => [...leadCampaignKey.all, 'list'] as const,
  list: (payload?: FixMeAny) => [...leadCampaignKey.lists(), payload] as const,
  details: () => [...leadCampaignKey.all, 'detail'] as const,
  detail: (id?: string) => [...leadCampaignKey.details(), id] as const,
  lookup: () => [...leadCampaignKey.all, 'lookup'] as const,
}

export const leadCampaignCostsKey = {
  all: ['lead-campaign-costs'] as const,
  lists: () => [...leadCampaignCostsKey.all, 'list'] as const,
  list: (payload?: FixMeAny) => [...leadCampaignKey.lists(), payload] as const,
  // details: () => [...leadCampaignCostsKey.all, 'detail'] as const,
  // detail: (id?: string) => [...leadCampaignKey.details(), id] as const,
  lookup: (payload: string) => [...leadCampaignCostsKey.all, 'lookup', payload] as const,
}

export const leadLayoutKey = {
  all: ['lead-layout'] as const,
  lists: () => [...leadLayoutKey.all, 'list'] as const,
  detail: () => [...leadLayoutKey.all, 'detail'] as const,
  personalized: (payload?: FixMeAny) => [...leadLayoutKey.detail(), payload] as const,
  section: () => [...leadLayoutKey.all, 'section'] as const,
}

export const leadContactKey = {
  all: ['lead-contact'] as const,
  lists: () => [...leadContactKey.all, 'list'] as const,
  list: (payload?: GetLeadContactsPayload) => [...leadContactKey.all, 'list', payload] as const,
}

export const leadDuplicateKey = {
  all: ['lead-duplicate'] as const,
  list: (payload?: CheckLeadDuplicatesV2Payload) => [...leadDuplicateKey.all, 'list', payload] as const,
  listPerLead: (payload?: GetLeadDuplicateListPayload) => [...leadDuplicateKey.all, 'list-per-lead', payload] as const,
  count: (leadId?: string) => [...leadDuplicateKey.all, 'count', leadId] as const,
  lists: () => [...leadDuplicateKey.all, 'list'] as const,
  detail: (id?: string) => [...leadDuplicateKey.all, 'detail', id] as const,
}

export const leadProcessKey = {
  all: ['lead-process'] as const,
  lists: () => [...leadProcessKey.all, 'list'] as const,
  list: (payload?: FixMeAny) => [...leadProcessKey.lists(), payload] as const,
  details: () => [...leadProcessKey.all, 'detail'] as const,
  detail: (id?: string) => [...leadProcessKey.details(), id] as const,
  config: () => [...leadProcessKey.all, 'config'] as const,
  lookup: () => [...leadProcessKey.all, 'lookup'] as const,
}

export const lookupKey = {
  all: ['lookup'] as const,
  filters: () => [...lookupKey.all, 'filters'] as const,
  filter: (payload?: FiltersRequestPayload) => [...lookupKey.filters(), payload] as const,
  lookups: () => [...lookupKey.all, 'lookup'] as const,
  lookup: (payload?: FixMeAny) => [...lookupKey.lookups(), payload] as const,
  durations: () => [...lookupKey.all, 'duration'] as const,
}

export const opportunityKey = {
  all: ['opportunity'] as const,
  lists: () => [...opportunityKey.all, 'list'] as const,
  list: (payload?: GetOpportunityListPayload) => [...opportunityKey.lists(), payload] as const,
  detail: (id?: string) => [...opportunityKey.all, 'detail', id] as const,
  lookup: () => [...opportunityKey.all, 'lookup'] as const,
  stat: () => [...opportunityKey.all, 'stat'] as const,
}

export const leadOpportunityKey = {
  all: ['lead-opportunity'] as const,
  lists: (payload: string) => [...leadOpportunityKey.all, payload] as const,
  list: (payload: GetLeadOpportunitiesPayload) => [...leadOpportunityKey.lists(payload.lead_id), payload] as const,
}

export const masterKey = {
  all: ['master'] as const,
  detail: (id: string) => [...masterKey.all, id] as const,
  current: () => [...masterKey.detail('current')] as const,
}

export const masterBranchKey = {
  all: ['master-branch'] as const,
  lists: () => [...masterBranchKey.all, 'list'] as const,
  list: (payload: string) => [...masterBranchKey.lists(), payload] as const,
}

export const salesObjectiveKey = {
  all: ['salesObjective'] as const,
  lists: () => [...salesObjectiveKey.all, 'list'] as const,
  list: (payload: GetSalesObjectivePayload) => [...salesObjectiveKey.lists(), payload] as const,
  details: () => [...salesObjectiveKey.all, 'detail'] as const,
  detail: (payload: GetSalesObjectiveDetailPayload) => [...salesObjectiveKey.details(), payload] as const,
}

export const salesPipelineKey = {
  all: ['salesPipeline'] as const,
  list: () => [...salesPipelineKey.all, 'list'] as const,
  salesPipelineByStatus: (payload: SalesPipelineByStatusPayload) => [...salesPipelineKey.all, payload] as const,
}

export const teamKey = {
  all: ['team'] as const,
  lists: () => [...teamKey.all, 'list'] as const,
  list: (payload?: FixMeAny) => [...teamKey.lists(), payload] as const,
  details: () => [...teamKey.all, 'detail'] as const,
  detail: (id?: string) => [...teamKey.details(), id] as const,
  memberLookups: () => [...teamKey.all, 'member-lookup'] as const,
  memberLookup: (teamId: string | null) => [...teamKey.memberLookups(), teamId] as const,
}

type AppSettingPayload = {
  master_id: string
  branch_id: string
  token: boolean
}
export const appSettingsKey = {
  all: ['appSettings'] as const,
  details: () => [...appSettingsKey.all, 'detail'] as const,
  detail: (payload: AppSettingPayload) => [...appSettingsKey.details(), payload] as const,
}

export const dashboardKey = {
  all: ['dashboard'] as const,
}

export const taskKey = {
  all: ['task'] as const,
  lists: () => [...taskKey.all, 'list'] as const,
  list: (payload: FixMeAny) => [...taskKey.lists(), payload] as const,
  listsFilter: () => [...taskKey.lists(), 'filter'] as const,
  listsStat: () => [...taskKey.lists(), 'stat'] as const,
}

export const calendarKey = {
  all: ['calendar'] as const,
  lists: () => [...calendarKey.all, 'list'] as const,
  list: (payload: FixMeAny) => [...calendarKey.lists(), payload] as const,
}

export const addressLookupKey = {
  all: ['address-lookup'] as const,
  countries: () => [...addressLookupKey.all, 'countries'] as const,
  states: () => [...addressLookupKey.all, 'states'] as const,
  state: (payload: number) => [...addressLookupKey.states(), payload] as const,
}

export const bulkUpdateLeads = {
  all: ['bulk-update-leads'] as const,
  lists: () => [...bulkUpdateLeads.all, 'list'] as const,
  list: (payload: GetBulkLeadsUpdateHistory.Request) => [...bulkUpdateLeads.lists(), payload] as const,
}

export const departmentKey = {
  all: ['department'] as const,
  lists: () => [...departmentKey.all, 'list'] as const,
}

export const emailSignatureKey = {
  detail: ['email-signature'] as const,
}

export const userActivityAuditLogKey = {
  all: ['user-activity-audit-log'] as const,
  lists: () => [...userActivityAuditLogKey.all, 'list'] as const,
  list: (payload?: FixMeAny) => [...userActivityAuditLogKey.lists(), payload] as const,
  lookup: (payload: string) => [...userActivityAuditLogKey.all, 'lookup', payload] as const,
}

export const upcomingEventKey = {
  all: ['upcoming-event'] as const,
  list: (payload: { start_date: string; end_date: string }) => [...upcomingEventKey.all, 'list', payload] as const,
  details: () => [...upcomingEventKey.all, 'detail'] as const,
  detail: (payload: { id: string }) => [...upcomingEventKey.details(), payload.id] as const,
}

export const rmConversionDashboardKey = {
  all: ['rm-conversion-dashboard'] as const,
  detail: (payload: FixMeAny) => [...rmConversionDashboardKey.all, payload] as const,
}

export const leadStagnationDashboardKey = {
  all: ['lead-stagnation-dashboard'] as const,
  detail: (payload: FixMeAny) => [...leadStagnationDashboardKey.all, payload] as const,
}

export const rmConversionDrilldownKey = {
  all: ['rm-conversion-drilldown'] as const,
  detail: (payload: FixMeAny) => [...rmConversionDrilldownKey.all, payload] as const,
}

export const leadStagnationDrilldownKey = {
  all: ['lead-stagnation-drilldown'] as const,
  detail: (payload: FixMeAny) => [...leadStagnationDrilldownKey.all, payload] as const,
}

export const rmEventsKey = {
  all: ['rm-events'] as const,
  detail: (payload: FixMeAny) => [...rmEventsKey.all, payload] as const,
}

export const rmEventsDrilldownKey = {
  all: ['rm-events-drilldown'] as const,
  detail: (payload: FixMeAny) => [...rmEventsDrilldownKey.all, payload] as const,
}

export const leadCaptureQualityDashboardKey = {
  all: ['lead-capture-quality-dashboard'] as const,
  detail: (payload: FixMeAny) => [...leadCaptureQualityDashboardKey.all, payload] as const,
}

export const leadFollowThroughDashboardKey = {
  all: ['lead-follow-through-dashboard'] as const,
  detail: (payload: FixMeAny) => [...leadFollowThroughDashboardKey.all, payload] as const,
}

export const leadCaptureQualityDrilldownKey = {
  all: ['lead-capture-quality-drilldown'] as const,
  detail: (payload: FixMeAny) => [...leadCaptureQualityDrilldownKey.all, payload] as const,
}

export const leadFollowThroughDrilldownKey = {
  all: ['lead-follow-through-drilldown'] as const,
  detail: (payload: FixMeAny) => [...leadFollowThroughDrilldownKey.all, payload] as const,
}

export const accessPermissionKey = {
  all: ['permission-access'] as const,
  detail: (leadId?: string) => [...accessPermissionKey.all, leadId] as const,
}

export const leadSearchKey = {
  all: ['lead-search'] as const,
  lists: (payload: any) => [...leadLayoutKey.all, 'list', payload] as const,
}
